import { useEffect, useMemo, useState } from 'react';
import { apiUrl } from '@territory/client/routes';
import { bootstrapWedgeAuth } from '@territory/client/api';
import { Splash } from './Splash';
import { BRAND, brandScreen, emeraldBtn, BrandMark, BrandWordmark, BrandStyleTag } from './wedgeBrand';

/**
 * Redeems an external magic-link token (wedge.repvantage.io/?magic=<token>).
 * Mirrors AuthCallbackWedge: the backend redeem endpoint sets the httpOnly
 * session cookie, then we bootstrap the wedge token bridge and hard-navigate
 * to the optional returnTo path the JWT carried (or / when none).
 *
 * 2026-06-02 · round 19 · V5 · distinguish expired vs invalid. Backend now
 * returns { reason: 'expired' | 'invalid' } in the 401 body. The "expired"
 * UX is reassuring (request a fresh one, takes 5 seconds) · the "invalid"
 * UX is more cautionary (something is wrong with this link · try the door
 * again rather than reusing the same link).
 *
 * 2026-06-02 · round 22 · per-user polish.
 *   - Decode the JWT payload (no verify) to surface the embedded email so
 *     the failed-redeem screen can offer ONE-TAP "send me a fresh link to
 *     <email>" instead of forcing the rep back to the landing to retype.
 *   - Updated copy · sign-in links no longer expire in 24h, they live for
 *     as long as the rep has access. Expired case is now reserved for
 *     legacy 24h-stamped tokens still in the wild.
 *   - Brand-aligned in-flight UI (Splash with the radar mark, already in
 *     use, but the failure container now mirrors the brand chrome).
 *
 * 2026-07-28 · travels with the magic-link expiry + single-use change in this
 * same PR. Three defects here would have turned that backend fix into a
 * support event, so they ship together rather than in sequence · there must be
 * no window where this screen and the server disagree:
 *   1. The round-22 copy told the rep their link failed because it predates
 *      "the update that makes them permanent". Links are now the opposite of
 *      permanent, so that sentence was exactly backwards.
 *   2. The one-tap resend CTA was gated on `tokenEmail`, decoded from an
 *      `email` claim that NEITHER mint site has ever signed (both sign
 *      { purpose, userId }). The gate was therefore always false and the
 *      button had never rendered once · on the very screen a dead link drops
 *      you on. It now always renders, and asks for the address when the token
 *      cannot supply one.
 *   3. The server can now answer reason:'used' for a replayed link. That value
 *      was unmapped and fell through to 'invalid', which accuses the rep of a
 *      corrupted link when they simply tapped a link they had already used.
 *
 * Deliberately NOT fixed by signing an `email` claim into the token: the whole
 * point of the expiry work is that this URL leaks into browser history, Referer
 * headers and forwarded inboxes. Putting the rep's address inside it would make
 * that leak worse.
 */
type FailReason = 'expired' | 'used' | 'invalid' | 'network';

function decodeJwtEmail(token: string): string {
  try {
    const parts = token.split('.');
    if (parts.length < 2) return '';
    const padded = parts[1] + '='.repeat((4 - (parts[1].length % 4)) % 4);
    const json = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
    const obj = JSON.parse(json) as { email?: unknown };
    return typeof obj.email === 'string' ? obj.email : '';
  } catch {
    return '';
  }
}

export function MagicRedeem({ token }: { token: string }) {
  const [failed, setFailed] = useState(false);
  const [reason, setReason] = useState<FailReason>('expired');
  const [freshState, setFreshState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  /* 2026-07-28 · the token cannot supply an address (no mint site signs an
     `email` claim, and we will not add one to a URL that leaks), so the rep
     types it here when we do not already know it. */
  const [typedEmail, setTypedEmail] = useState('');

  /* round 22 · best-effort email decode from the failed token so the
     fresh-link CTA can target it without making the rep retype. We do NOT
     verify the signature here · this is purely a UX hint. */
  const tokenEmail = useMemo(() => decodeJwtEmail(token), [token]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(apiUrl('/api/auth/wedge-access/redeem'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ token }),
        });
        if (!res.ok) {
          /* V5 · read the reason field from the 401 body. Falls back to
             'invalid' for anything we can't parse (non-2xx without JSON =
             likely network / proxy issue, treat as generic). */
          let resolved: FailReason = 'invalid';
          try {
            const body = (await res.json()) as { reason?: unknown };
            if (typeof body.reason === 'string') {
              if (body.reason === 'expired') resolved = 'expired';
              /* 2026-07-28 · single-use links answer 'used' on a replay. Without
                 this it fell through to 'invalid', which tells a rep their link
                 is corrupted when they just tapped one they had already spent. */
              else if (body.reason === 'used') resolved = 'used';
              else if (body.reason === 'invalid') resolved = 'invalid';
            }
          } catch { /* non-JSON · keep invalid */ }
          if (alive) {
            setReason(resolved);
            setFailed(true);
          }
          return;
        }
        /* 2026-06-02 · universal access round 18 · U6. Honor the optional
           returnTo path the magic JWT carried. */
        let returnTo = '/';
        try {
          const data = (await res.clone().json()) as { returnTo?: string };
          if (typeof data.returnTo === 'string') {
            const trimmed = data.returnTo.trim().slice(0, 400);
            if (/^\/[^/]/.test(trimmed) || trimmed === '/') returnTo = trimmed;
          }
        } catch { /* fall back to / */ }
        /* 07-27 remediation audit · a redeem is a possible identity swap:
           drop the previous rep's offline session cache (setWedgeTokens no
           longer clears it, and this flow may stay cookie-only anyway). */
        try { localStorage.removeItem('rv_auth_user_cache'); } catch { /* noop */ }
        await bootstrapWedgeAuth().catch(() => {
          /* non-fatal · cookie is already set */
        });
        if (alive) window.location.replace(returnTo);
      } catch {
        if (alive) {
          setReason('network');
          setFailed(true);
        }
      }
    })();
    return () => {
      alive = false;
    };
  }, [token]);

  if (!failed) return <Splash label="Signing you in" />;

  /* V5 · different copy per reason. 2026-07-28 · rewritten for expiring
     single-use links. The round-22 text claimed links were permanent, which
     is now the opposite of true. */
  const copy = reason === 'used'
    ? {
        headline: 'This sign-in link was already used',
        body: 'Sign-in links work once, so this one is spent. Nothing is wrong with your account · get a fresh link below.',
        cta: 'Send me a fresh link',
      }
    : reason === 'expired'
    ? {
        headline: 'This sign-in link has expired',
        body: 'Sign-in links last 15 minutes and work once, which keeps a forwarded email from becoming a way into your account. Get a fresh one below · takes five seconds.',
        cta: 'Send me a fresh link',
      }
    : reason === 'invalid'
    ? {
        headline: 'This sign-in link is not valid',
        body: 'The link may have been copied incomplete or modified. Request a new one and we will send a working link to your inbox.',
        cta: 'Send me a fresh link',
      }
    : {
        headline: 'We could not sign you in',
        body: 'A network blip dropped the request. Try again, or use Continue with Google.',
        cta: 'Try again',
      };

  /* 2026-07-28 · the fresh-link CTA now works. Round 22 wired it to an email
     decoded out of the token, but no mint site has ever signed an `email`
     claim, so `tokenEmail` was always '' and the whole button was gated off.
     The rep landed on a dead end and had to find the sign-in page themselves.

     Now: use the token's email if one is ever present, otherwise ask. The
     address is only needed because we deliberately refuse to put it in the
     link · see the header note. */
  const recipient = (tokenEmail || typedEmail).trim().toLowerCase();
  const recipientValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(recipient);

  async function sendFreshLink() {
    if (reason === 'network') {
      window.location.reload();
      return;
    }
    if (!recipientValid) return;
    setFreshState('sending');
    try {
      const res = await fetch(apiUrl('/api/auth/wedge-access/email-me'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: recipient }),
      });
      /* The endpoint always answers 201 so nobody can enumerate which
         addresses are registered. A non-ok here is a transport failure, not
         "unknown email", and the copy below says so. */
      setFreshState(res.ok ? 'sent' : 'error');
    } catch {
      setFreshState('error');
    }
  }

  return (
    <div style={{ ...brandScreen, gap: 14 }}>
      <BrandStyleTag />
      <BrandMark size={70} />
      <BrandWordmark size={18} />
      <div style={{ fontSize: 18, fontWeight: 700, color: BRAND.text }}>
        {copy.headline}
      </div>
      <div style={{ fontSize: 14, color: BRAND.textSec, maxWidth: 340, lineHeight: 1.5 }}>
        {copy.body}
      </div>
      {freshState === 'sent' ? (
        <div style={{
          marginTop: 6,
          padding: '12px 22px',
          borderRadius: 12,
          background: 'rgba(52,211,153,0.12)',
          border: '1px solid rgba(52,211,153,0.45)',
          color: '#A7F3D0',
          fontSize: 14,
          fontWeight: 600,
          maxWidth: 360,
          lineHeight: 1.5,
        }}>
          Check {recipient}. Your fresh link is on the way, and it works for 15 minutes.
        </div>
      ) : reason !== 'network' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 6, width: '100%', maxWidth: 340 }}>
          {!tokenEmail ? (
            <input
              type="email"
              inputMode="email"
              autoComplete="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              value={typedEmail}
              onChange={(e) => setTypedEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && recipientValid) sendFreshLink(); }}
              placeholder="you@company.com"
              aria-label="Your work email"
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: '13px 16px',
                minHeight: 44,
                borderRadius: 12,
                border: '1px solid rgba(255,255,255,0.18)',
                background: 'rgba(255,255,255,0.06)',
                color: BRAND.text,
                fontSize: 15,
                outline: 'none',
              }}
            />
          ) : null}
          <button
            className="wedge-cta-emerald"
            onClick={sendFreshLink}
            disabled={freshState === 'sending' || !recipientValid}
            style={{
              ...emeraldBtn(freshState === 'sending' || !recipientValid),
              width: '100%',
              padding: '13px 26px',
              cursor: freshState === 'sending' ? 'wait' : recipientValid ? 'pointer' : 'not-allowed',
              minHeight: 44,
            }}
          >
            {freshState === 'sending'
              ? 'Sending...'
              : tokenEmail
              ? `Send a fresh link to ${tokenEmail}`
              : 'Send me a fresh link'}
          </button>
          {freshState === 'error' ? (
            <div style={{ fontSize: 13, color: '#FCA5A5', lineHeight: 1.5 }}>
              We could not reach the server. Check your connection and try again.
            </div>
          ) : null}
        </div>
      ) : (
        <button
          className="wedge-cta-emerald"
          onClick={() => window.location.replace('/')}
          style={{
            ...emeraldBtn(false),
            marginTop: 6,
            width: 'auto',
            padding: '13px 26px',
            minHeight: 44,
            minWidth: 200,
          }}
        >
          {copy.cta}
        </button>
      )}
      {freshState === 'error' && (
        <div style={{ fontSize: 12, color: BRAND.redText, marginTop: 4 }}>
          Could not send a fresh link. Try Continue with Google instead.
        </div>
      )}
    </div>
  );
}
